import {
  type BaseComponent,
  type SbBlokData,
  storyblokEditable,
} from '@cinch-nx/data-storyblok'
import {
  type ComponentLayouts,
  StoryblokRendererV2 as StoryblokRenderer,
} from '@cinch-nx/storyblok-utils'
import cx from 'classnames'

import styles from './landing-hero-v2.module.css'

export interface LandingHeroV2Props extends BaseComponent {
  title?: string
  body?: string
  badge?: SbBlokData[]
  logo?: SbBlokData[]
  image?: SbBlokData[]
  buttons?: SbBlokData[]
  /**
   * Components for the hero's bloks, injected by the app that owns them.
   * TODO: temporary, see LayoutProps.heroComponents.
   */
  heroComponents?: ComponentLayouts
}

/**
 * Whether a Storyblok asset field holds a file: an empty one still arrives,
 * with filename ''. SbBlokData does not type the field as an asset, so it is
 * narrowed before its filename is read.
 */
const hasFile = (asset: unknown) =>
  typeof asset === 'object' &&
  asset !== null &&
  'filename' in asset &&
  Boolean(asset.filename)

interface DsButtonBlok extends SbBlokData {
  label: string
  url: string
}

/** A DS_Button blok with the label and url a link needs */
const isDsButton = (blok: SbBlokData): blok is DsButtonBlok =>
  blok.component === 'DS_Button' &&
  typeof blok.label === 'string' &&
  Boolean(blok.label) &&
  typeof blok.url === 'string' &&
  Boolean(blok.url)

export const LandingHeroV2: React.FC<LandingHeroV2Props> = ({
  title,
  body,
  badge,
  logo,
  image,
  buttons,
  heroComponents,
}) => {
  // TODO: temporary. The badge field still accepts the old `badge` blok,
  // which is ignored; drop the component check once it has left the
  // LandingHero_V2 whitelist and no story still contains one.
  const dsBadge = badge?.find(
    (blok) => blok.component === 'DS_Badge' && (blok.text || blok.icon),
  )
  // TODO: temporary. The logo field still accepts Image_v2, which is ignored;
  // drop the component check once it has left the LandingHero_V2 whitelist
  // and no story still contains one.
  const dsLogo = logo?.find(
    (blok) => blok.component === 'DS_Logo' && blok.brand,
  )
  // TODO: temporary. The image field still accepts Image_v2, which is ignored;
  // drop the component check once it has left the LandingHero_V2 whitelist
  // and no story still contains one.
  const dsImage = image?.find(
    (blok) => blok.component === 'DS_Image' && hasFile(blok.image),
  )
  // Figma has two slots, both On: Dark BG, Size: Medium and without icons.
  // The shared DS_Button renderer draws a small outlined button and reports
  // every click as an AI search click, so the hero draws the link itself.
  // TODO: swap the <a> for the DS v3 Button once it renders in servicing.
  const dsButtons = buttons?.filter(isDsButton).slice(0, 2) ?? []

  return (
    <div className={styles.hero}>
      <div className={styles.inner}>
        <div className={styles.content}>
          {dsBadge && heroComponents && (
            <StoryblokRenderer block={dsBadge} components={heroComponents} />
          )}
          {dsLogo && heroComponents && (
            <StoryblokRenderer block={dsLogo} components={heroComponents} />
          )}
          <div className={styles.text}>
            {title && <h1 className={styles.title}>{title}</h1>}
            {body && <p className={styles.body}>{body}</p>}
          </div>
          {dsButtons.length > 0 && (
            <div className={styles.actions}>
              {dsButtons.map((blok) => (
                <a
                  {...storyblokEditable(blok)}
                  key={blok._uid}
                  className={cx(
                    styles.button,
                    blok.variant === 'secondary'
                      ? styles.outlined
                      : styles.filled,
                  )}
                  href={blok.url}
                >
                  {blok.label}
                </a>
              ))}
            </div>
          )}
        </div>
        {/* Rendered even without an image, to keep the two-column split */}
        <div
          className={styles.media}
          aria-hidden={dsImage && heroComponents ? undefined : true}
        >
          {dsImage && heroComponents && (
            <StoryblokRenderer block={dsImage} components={heroComponents} />
          )}
        </div>
      </div>
    </div>
  )
}
